import { Controller } from "@hotwired/stimulus"
import { lineDiff } from "./line_diff"

export default class extends Controller {
    static targets = ["item", "group", "empty", "panel", "title", "description", "source", "status", "save", "discard", "inherit", "errors", "errorList", "settingsError", "notification", "errorToast", "successToast", "globalPreview", "workspace", "mode", "editingPanel", "historyPanel", "historyStatus", "revision", "historyMode", "historyContent", "older", "retry", "picker", "comparisonControls", "comparison", "comparisonHint", "copy", "copyStatus", "scope", "liquidPanel", "jsonPanel"]
    static values = { entries: Array, url: String, customer: Boolean, messages: Object }

    connect() {
        this.scopePath = this.scopeTarget.value
        this.historyCache = new Map()
        this.activeMode = "editor"
        this.diffMode = "text"
        this.drafts = new Map(this.entriesValue.map(entry => [entry.key, { ...entry, errors: null }]))
        this.activeKey = new URL(location.href).searchParams.get("prompt")
        if (!this.drafts.has(this.activeKey)) this.activeKey = this.entriesValue[0].key
        this.onChange = event => {
            if (this.loading || event.target !== this.textarea) return
            this.current.content = this.textarea.value
            this.current.inherited = this.customerValue && this.current.content.trim() === ""
            this.current.errors = null
            this.renderErrors()
            this.renderStatus()
        }
        this.element.addEventListener("change", this.onChange)
        this.onEditorReady = () => { if (this.element.isConnected) this.render() }
        this.element.addEventListener("views--components--form--input--liquid-editor:ready", this.onEditorReady)
        this.element.addEventListener("views--components--form--input--json-editor:ready", this.onEditorReady)
        queueMicrotask(() => { if (this.element.isConnected) this.render() })
    }

    disconnect() {
        this.historyAbort?.abort()
        this.element.removeEventListener("change", this.onChange)
        this.element.removeEventListener("views--components--form--input--liquid-editor:ready", this.onEditorReady)
        this.element.removeEventListener("views--components--form--input--json-editor:ready", this.onEditorReady)
    }

    get textarea() { return this.editor.textareaTarget }
    get current() { return this.drafts.get(this.activeKey) }
    get original() { return this.entriesValue.find(entry => entry.key === this.activeKey) }
    get editor() {
        const name = this.current.format === "json" ? "views--components--form--input--json-editor" : "views--components--form--input--liquid-editor"
        return this.application.getControllerForElementAndIdentifier(this.element.querySelector(`[data-controller='${name}']`), name)
    }

    isDirty() {
        return this.entriesValue.some(entry => this.drafts.get(entry.key).content !== entry.content)
    }

    switchScope(event) {
        const destination = event.currentTarget.value
        if (destination === this.scopePath) return
        if (this.saving || (this.isDirty() && !confirm(this.messagesValue.leave))) {
            event.currentTarget.value = this.scopePath
            return
        }
        const url = new URL(destination, location.origin)
        url.searchParams.set("prompt", this.activeKey)
        this.scopeNavigation = true
        this.navigateScope(url.href)
    }

    navigateScope(url) { location.assign(url) }

    select(event) {
        if (this.saving) return
        this.activeKey = event.currentTarget.dataset.key || event.currentTarget.value
        const url = new URL(location.href)
        url.searchParams.set("prompt", this.activeKey)
        history.replaceState(null, "", url)
        this.render()
        this.panelTarget.scrollIntoView({ block: "start", behavior: "instant" })
    }

    render() {
        this.liquidPanelTarget.classList.toggle("hidden", this.current.format === "json")
        this.jsonPanelTarget.classList.toggle("hidden", this.current.format !== "json")
        this.pickerTarget.value = this.activeKey
        const item = this.itemTargets.find(item => item.dataset.key === this.activeKey)
        this.titleTarget.textContent = item.querySelector("span").textContent
        this.descriptionTarget.textContent = this.current.format === "json" ? this.messagesValue.description_exa : this.messagesValue[`description_${this.current.group}`]
        this.sourceTarget.textContent = this.customerValue
            ? (this.current.inherited ? this.messagesValue.inherited : this.messagesValue.override)
            : this.messagesValue.global
        if (this.hasInheritTarget) this.inheritTarget.checked = this.current.inherited
        if (this.hasGlobalPreviewTarget) this.globalPreviewTarget.textContent = this.current.default || this.messagesValue.no_global
        if (this.editor.view) {
            this.editor.view.scrollDOM.style.minHeight = "0"
            this.editor.view.scrollDOM.style.maxHeight = "none"
        }
        this.loading = true
        this.editor.setContent(this.current.inherited ? this.current.default : this.current.content, { readOnly: !!this.saving || this.current.inherited })
        this.loading = false
        this.renderErrors()
        this.renderStatus()
        if (this.activeMode === "history") this.loadHistory()
    }

    renderStatus() {
        this.statusTarget.textContent = this.saving ? this.messagesValue.saving : (this.current.content !== this.original.content ? this.messagesValue.dirty : this.messagesValue.clean)
        const unchanged = this.current.content === this.original.content
        this.saveTarget.disabled = !!this.saving || unchanged
        this.discardTarget.disabled = !!this.saving || unchanged
        this.pickerTarget.disabled = !!this.saving
        this.itemTargets.forEach(item => {
            const key = item.dataset.key, draft = this.drafts.get(key)
            const changed = draft.content !== this.entriesValue.find(entry => entry.key === key).content
            item.setAttribute("aria-pressed", String(key === this.activeKey))
            item.classList.toggle("bg-secondary", key === this.activeKey)
            item.classList.toggle("text-gray-0", key === this.activeKey)
            item.classList.toggle("text-secondary", key !== this.activeKey)
            item.classList.toggle("hover:bg-gray-200", key !== this.activeKey)
            item.querySelector("[data-marker]").textContent = draft.errors ? "!" : changed ? "●" : ""
            item.title = draft.errors ? this.messagesValue.invalid : changed ? this.messagesValue.dirty : ""
            item.disabled = !!this.saving
        })
    }

    filter(event) {
        const query = event.target.value.toLocaleLowerCase().trim()
        this.itemTargets.forEach(item => { item.hidden = !item.textContent.toLocaleLowerCase().includes(query); item.classList.toggle("hidden", item.hidden) })
        this.groupTargets.forEach(group => { group.hidden = [...group.querySelectorAll("button")].every(item => item.hidden) })
        this.emptyTarget.classList.toggle("hidden", this.itemTargets.some(item => !item.hidden))
    }

    inherit() {
        if (this.saving) return
        if (this.inheritTarget.checked) this.current.overrideDraft = this.current.content
        this.current.inherited = this.inheritTarget.checked
        this.current.content = this.current.inherited ? "" : (this.current.overrideDraft || this.current.default)
        this.current.errors = null
        this.render()
    }

    discard() {
        if (this.saving) return
        this.drafts.set(this.activeKey, { ...this.original, errors: null })
        this.render()
    }

    insert(event) {
        if (!this.current.inherited && !this.saving) this.editor.insertVariable(event.currentTarget.dataset.value)
    }

    renderErrors() {
        const errors = this.current.errors || {}
        this.errorsTarget.classList.toggle("hidden", !Object.keys(errors).length)
        this.errorListTarget.replaceChildren()
        Object.entries(errors).forEach(([key, messages]) => {
            const li = document.createElement("li")
            li.textContent = `${key === this.activeKey ? this.titleTarget.textContent : (this.current.errorLabels?.[key] || key.replaceAll("_", " "))}: ${messages.join(", ")}`
            this.errorListTarget.append(li)
        })
        this.settingsErrorTarget.hidden = !Object.keys(errors).some(key => key !== this.activeKey)
        const host = this.textarea.parentElement
        host.classList.toggle("border-red", !!errors[this.activeKey])
        host.classList.toggle("border-gray-300", !errors[this.activeKey])
        const content = host.querySelector(".cm-content")
        content?.setAttribute("aria-invalid", String(!!errors[this.activeKey]))
        this.errorsTarget.id = "prompt-library-errors"
        content?.setAttribute("aria-describedby", "prompt-library-errors")
        content?.setAttribute("aria-label", this.titleTarget.textContent)
    }

    async save(event) {
        event.preventDefault()
        if (this.saving) return
        this.saving = true
        this.renderStatus()
        // Freeze the editor while saving so a late response cannot discard newer typing.
        this.loading = true
        this.editor.setContent(this.current.inherited ? this.current.default : this.current.content, { readOnly: true })
        this.loading = false
        try {
            const url = new URL(this.urlValue, location.origin)
            url.pathname += `/${encodeURIComponent(this.activeKey)}`
            const response = await fetch(url, {
                method: "PATCH", signal: AbortSignal.timeout(30000), headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content },
                body: JSON.stringify({ prompt: { content: this.current.content, revision: this.current.revision } })
            })
            if (response.redirected) throw new Error("Session expired")
            const result = await response.json()
            if (!response.ok) {
                this.current.errorLabels = result.error_labels
                this.current.errors = result.errors || { [this.activeKey]: [this.messagesValue.network] }
                this.toast(false)
            } else {
                this.entriesValue = this.entriesValue.map(entry => entry.key === this.activeKey ? { ...entry, ...result.entry } : entry)
                this.drafts.set(this.activeKey, { ...this.original, errors: null })
                this.historyCache.delete(this.activeKey)
                this.toast(true)
            }
        } catch {
            this.current.errors = { [this.activeKey]: [this.messagesValue.network] }
            this.toast(false)
        } finally {
            this.saving = false
            this.render()
            if (this.current.errors) {
                this.errorsTarget.scrollIntoView({ block: "center", behavior: "instant" })
                this.editor.focus()
            }
        }
    }

    switchMode(event) {
        if (this.saving) return
        this.activeMode = event.currentTarget.dataset.mode
        this.modeTargets.forEach(tab => {
            const active = tab.dataset.mode === this.activeMode
            tab.setAttribute("aria-selected", String(active))
            tab.classList.toggle("bg-gray-200", active)
        })
        this.editingPanelTarget.classList.toggle("hidden", this.activeMode !== "editor")
        this.historyPanelTarget.classList.toggle("hidden", this.activeMode !== "history")
        if (this.activeMode === "history") this.loadHistory()
        else this.editor.view?.requestMeasure()
    }

    switchHistoryMode(event) {
        this.diffMode = event.currentTarget.dataset.mode
        this.historyModeTargets.forEach(button => {
            const active = button.dataset.mode === this.diffMode
            button.setAttribute("aria-pressed", String(active))
            button.classList.toggle("bg-gray-200", active)
        })
        this.showRevision()
    }

    async loadHistory(append = false) {
        const key = this.activeKey
        const cached = this.historyCache.get(key)
        this.historyAbort?.abort()
        this.historyAbort = null
        this.retryTarget.classList.add("hidden")
        this.revisionTarget.disabled = false
        this.olderTarget.disabled = false
        if (!append && cached) { this.renderHistory(cached); return }
        this.historyAbort = new AbortController()
        const request = this.historyAbort
        this.historyStatusTarget.textContent = this.messagesValue.history_loading
        this.revisionTarget.disabled = true
        this.olderTarget.disabled = true
        this.historyContentTarget.textContent = ""
        this.copyTarget.disabled = true
        this.copyStatusTarget.textContent = ""
        this.comparisonTarget.disabled = true
        try {
            const url = new URL(this.urlValue, location.origin)
            url.pathname += `/${encodeURIComponent(key)}/histories`
            if (append && cached?.next_cursor) url.searchParams.set("before", cached.next_cursor)
            const response = await fetch(url, { headers: { Accept: "application/json" }, signal: request.signal })
            if (!response.ok || response.redirected) throw new Error("History unavailable")
            const result = await response.json()
            if (request !== this.historyAbort || key !== this.activeKey) return
            const data = { entries: [...(append ? cached.entries : []), ...result.entries], next_cursor: result.next_cursor }
            this.historyCache.set(key, data)
            this.renderHistory(data)
        } catch (error) {
            if (error.name === "AbortError" || request !== this.historyAbort || key !== this.activeKey) return
            this.retryTarget.classList.remove("hidden")
            this.historyStatusTarget.textContent = this.messagesValue.history_failed
            this.revisionTarget.replaceChildren()
            this.olderTarget.classList.add("hidden")
        } finally {
            if (request === this.historyAbort) {
                this.revisionTarget.disabled = false
                this.olderTarget.disabled = false
            }
        }
    }

    retryHistory() { this.loadHistory() }

    loadOlder() { this.loadHistory(true) }

    renderHistory(data) {
        const previous = this.revisionTarget.value
        this.revisionTarget.replaceChildren()
        data.entries.forEach(entry => {
            const option = document.createElement("option")
            option.value = entry.id
            option.textContent = `${new Date(entry.created_at).toLocaleString()} · #${entry.id}`
            this.revisionTarget.append(option)
        })
        if (data.entries.some(entry => String(entry.id) === previous)) this.revisionTarget.value = previous
        this.historyStatusTarget.textContent = data.entries.length ? this.messagesValue.history_timezone : this.messagesValue.history_empty
        this.olderTarget.classList.toggle("hidden", !data.next_cursor)
        this.showRevision()
    }

    get selectedRevision() {
        return this.historyCache.get(this.activeKey)?.entries.find(item => String(item.id) === this.revisionTarget.value)
    }

    revisionComparison(entry) {
        const entries = this.historyCache.get(this.activeKey).entries
        const index = entries.indexOf(entry)
        switch (this.comparisonTarget.value) {
            case "next": return index > 0 ? { before: entry.after, after: entries[index - 1].after } : null
            case "current": return { before: entry.after, after: this.original.content }
            default: return { before: entry.before, after: entry.after }
        }
    }

    async copyRevision() {
        const entry = this.selectedRevision
        if (!entry || this.copyTarget.disabled) return
        const key = this.activeKey
        this.copyTarget.disabled = true
        try {
            await navigator.clipboard.writeText(entry.after)
            if (key === this.activeKey && entry === this.selectedRevision) this.copyStatusTarget.textContent = this.messagesValue.copied
        } catch {
            if (key === this.activeKey && entry === this.selectedRevision) this.copyStatusTarget.textContent = this.messagesValue.copy_failed
        } finally {
            if (key === this.activeKey && entry === this.selectedRevision) this.copyTarget.disabled = false
        }
    }

    showRevision() {
        const entry = this.selectedRevision
        this.historyContentTarget.replaceChildren()
        this.copyStatusTarget.textContent = ""
        this.copyTarget.disabled = !entry
        this.comparisonTarget.disabled = !entry
        this.copyTarget.classList.toggle("hidden", this.diffMode !== "text")
        this.comparisonControlsTarget.classList.toggle("hidden", this.diffMode === "text")
        this.comparisonHintTarget.textContent = this.messagesValue[`comparison_${this.comparisonTarget.value}`]
        if (!entry) return
        if (this.diffMode === "text") {
            this.historyContentTarget.textContent = entry.after.trim() ? entry.after : this.messagesValue.history_blank
            return
        }
        const comparison = this.revisionComparison(entry)
        if (!comparison) {
            this.historyContentTarget.textContent = this.messagesValue.no_next
            return
        }
        if (comparison.before === comparison.after) {
            this.historyContentTarget.textContent = this.messagesValue.history_unchanged
            return
        }
        lineDiff(comparison.before, comparison.after).forEach(line => {
            const row = document.createElement("div"), sign = document.createElement("span"), text = document.createElement("span")
            row.className = `prompt-diff-line prompt-diff-${line.kind}`
            sign.textContent = line.kind === "added" ? "+" : line.kind === "removed" ? "−" : " "
            sign.setAttribute("aria-label", this.messagesValue[`history_${line.kind}`])
            text.textContent = line.text
            row.append(sign, text)
            this.historyContentTarget.append(row)
        })
    }

    toast(success) {
        this.notificationTarget.replaceChildren((success ? this.successToastTarget : this.errorToastTarget).content.cloneNode(true))
    }

    beforeUnload(event) {
        if (this.scopeNavigation || !this.isDirty()) return
        event.preventDefault()
        event.returnValue = ""
    }

    beforeVisit(event) {
        if (this.isDirty() && !confirm(this.messagesValue.leave)) event.preventDefault()
    }
}
